type PolicySectionProps = {
  title: string
  number: number
  children: React.ReactNode
}

const PolicySection = ({ title, number, children }: PolicySectionProps) => {
  return (
    <div className="flex flex-col gap-y-2">
      <h3 className="inline-flex items-center gap-x-2 text-body-bold">
        <span>{number}.</span>
        <span>{title}</span>
      </h3>
      {children}
    </div>
  )
}

const ParagraphWithIndent = ({ children }: { children: React.ReactNode }) => {
  return <p className="ml-6">{children}</p>
}

export const PrivacyPolicyContent = () => {
  return (
    <div className="flex flex-col gap-y-10 text-body">
      <p>
        株式会社トランスリアル（以下「当社」といいます。）は、当社が運営するシェアオフィスやコワーキングスペースに関する情報を記載したポータルサイト「officeMAP」に関するお客様の個人情報の取得並びに適正な利用とその管理の徹底に努め、次に掲げる方針に基づき、個人情報を取り扱うものとします。なお、本プライバシーポリシーにおいて別段の定めがない限り、本プライバシーポリシーにおける用語の定義は、個人情報保護法の定めに従います。また、当社グループとは、株式会社Tranceホールディングス、株式会社
        TranceTECH Capitalを意味します。
      </p>

      <PolicySection number={1} title="個人情報の取得について">
        <p>
          当社は、個人情報（個人情報に加え、個人情報に該当しない個人に関する情報を含みます。以下同じです。）を収集する場合は、あらかじめ定めた、あるいは取得の際に示した利用目的の範囲内で適法かつ公正な手段により収集します。
        </p>
      </PolicySection>

      <PolicySection number={2} title="個人情報の利用目的について">
        <p>
          当社は、取得した個人情報を、下記(1)に掲げる当社の業務のために、下記(2)に掲げる目的の範囲内で、かつ業務の遂行上必要な限りにおいて利用します。
        </p>
        <div className="flex flex-col gap-y-2">
          <p>(1) 個人情報を利用する当社の業務内容</p>
          <ul className="list-disc pl-4">
            <li>不動産の売買、交換、賃貸及びその仲介並びに所有、管理、利用</li>
            <li>賃貸借契約にかかる保証業務</li>
            <li>経営に関するコンサルティング業務</li>
            <li>
              各種企業経営の合理化、健全化に関する情報提供サービス業及び情報処理サービス業
            </li>
            <li>市場調査及び各種マーケティングリサーチ業務</li>
            <li>損害保険代理業</li>
            <li>生命保険の募集に関する業務</li>
            <li>前各号に付帯する一切の業務</li>
          </ul>
        </div>

        <div className="flex flex-col gap-y-2">
          <p>(2) 個人情報の利用目的</p>
          <ul className="list-disc pl-4">
            <li>本サービスの提供、サービスの開発・改善</li>
            <li>不動産事業に関する情報・サービスの提供</li>
            <li>会員登録サービスにおける顧客管理</li>
            <li>
              お客様との「建物賃貸借契約」「不動産売買契約」等を含む各種契約の履行
            </li>
            <li>
              契約後の契約管理・家賃管理・建物管理・緊急対応等、アフターサービスの実施
            </li>
            <li>保険代理業等の遂行及びサービスのご提供</li>
            <li>ご契約時の各種保険、引越、サポートサービス等の提供</li>
            <li>各種お問い合わせへの対応</li>
            <li>新規商品開発および新規サービス検討のため。</li>
            <li>上記各事項に付随関連する目的</li>
          </ul>
        </div>
      </PolicySection>

      <PolicySection number={3} title="個人情報の安全管理について">
        <p>
          当社は、個人データの紛失、破壊、改ざんおよび漏洩などを防止するため、不正アクセス、コンピュータウィルス等に対する適切な情報セキュリティ対策を講じます。
        </p>
      </PolicySection>

      <PolicySection number={4} title="個人情報の第三者提供について">
        <p>
          当社は、第5条の定めに従って共同利用する場合その他の法令に定められている場合を除き、ご本人の同意を得ることなく個人情報を第三者に提供しません。
        </p>
      </PolicySection>

      <PolicySection number={5} title="個人情報の共同利用について">
        <p>
          当社は、以下のとおり個人情報を共同利用し、共同利用される個人情報を下記に定める利用者に提供いたします。
        </p>
        <div>
          <p>(1) 共同して利用される情報の項目</p>
          <ParagraphWithIndent>第1条記載の個人情報</ParagraphWithIndent>
          <p>(2) 共同して利用する者の範囲</p>
          <ParagraphWithIndent>当社グループ</ParagraphWithIndent>
          <p>(3) 利用する者の利用目的</p>
          <ParagraphWithIndent>第2条(2)記載の目的</ParagraphWithIndent>
          <p>
            (4) 上記情報の管理について責任を有する者の名称、住所及び代表者氏名
          </p>
          <ParagraphWithIndent>株式会社トランスリアル</ParagraphWithIndent>
        </div>
      </PolicySection>

      <PolicySection number={6} title="個人情報の委託について">
        <p>
          当社は、業務を円滑に遂行するため業務の一部を委託先に委託し、当該委託先に対し必要な範囲で、業務とそれに関連する個人情報を委託する場合があります。この場合は、当社が定めた基準を満たすものを委託先として選定し、機密保持と個人情報の取扱いに関する取り決めを明示した業務委託契約の締結を行います。
        </p>
      </PolicySection>

      <PolicySection number={7} title="個人情報の開示・訂正等について">
        <p>
          当社は、個人情報の利用目的の開示、訂正、利用の停止等を求められた場合、ならびに苦情およびご相談のお申し出を受けた場合には、適切かつ迅速に対処できるよう努めてまいります。
        </p>
      </PolicySection>

      <PolicySection number={8} title="プライバシーポリシーの改定について">
        <p>
          当社は、個人情報および特定個人情報を適切かつ安全に保護するために継続的に改善を図ってまいります。そのため「プライバシーポリシー」も予告なく更新・改訂させていただくことがございますので、予めご了承願います。なお、改訂した場合は、当ウェブサイトにて速やかにお知らせします。
        </p>
      </PolicySection>

      <PolicySection number={9} title="個人情報に関するお問い合わせ">
        <p>
          個人情報の取り扱いに関するお問い合わせは、下記のお問い合わせ先よりお問い合わせください。
        </p>
        <div>
          <p>受付窓口</p>
          <ParagraphWithIndent>
            〒150-0002 東京都渋谷区渋谷2-2-17 トランスワークス青山4F
          </ParagraphWithIndent>
          <ParagraphWithIndent>
            株式会社トランスリアル 総務部
          </ParagraphWithIndent>
          <ParagraphWithIndent>MAIL：info@trancereal.co.jp</ParagraphWithIndent>
          <p>受付時間</p>
          <ParagraphWithIndent>
            10:00〜18:00（土曜、日曜、祝日、年末年始、その他当社の指定休日を除きます）
          </ParagraphWithIndent>
        </div>
      </PolicySection>
    </div>
  )
}
